import { useCallback, useMemo, useRef, useState } from "react";

// Browser speech-to-text (Web Speech API)
// Works in Chromium-based browsers. On unsupported browsers, this hook becomes a no-op.
export function useSpeechToText({ lang = "en-US" } = {}) {
  const Recognition =
    window.SpeechRecognition || window.webkitSpeechRecognition || null;

  const supported = Boolean(Recognition);
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);

  const start = useCallback(
    ({ onFinalText } = {}) => {
      if (!supported) {
        setError("Voice input is not supported in this browser.");
        return;
      }

      setError(null);
      const rec = new Recognition();
      recognitionRef.current = rec;
      rec.lang = lang;
      rec.interimResults = true;
      rec.continuous = false;

      let finalText = "";

      rec.onstart = () => setListening(true);
      rec.onerror = (e) => {
        setError(e?.error || "Voice input error");
        setListening(false);
      };
      rec.onend = () => {
        setListening(false);
        if (finalText && typeof onFinalText === "function") {
          onFinalText(finalText.trim());
        }
      };

      rec.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const res = event.results[i];
          const t = res[0]?.transcript || "";
          if (res.isFinal) finalText += t + " ";
          else interim += t;
        }
      };

      try {
        rec.start();
      } catch (e) {
        setError("Unable to start voice input.");
        setListening(false);
      }
    },
    [Recognition, lang, supported],
  );

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop?.();
    } catch {
      // ignore
    }
  }, []);

  return useMemo(
    () => ({ supported, listening, error, start, stop }),
    [supported, listening, error, start, stop],
  );
}
